import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import GraphQL from '~/types/graphql';

import {
	ASSETS_STORAGE_VERSIONLESS,
} from '~/config';

import Button, {
	ButtonSize,
	ButtonStyle,
} from '~/components/patterns/buttons/Button';
import ButtonsLayout from '~/components/patterns/buttons/ButtonsLayout';
import CancelButton from '~/components/app/CancelButton';
import ExportPanel from '~/components/patterns/layout/ExportPanel';
import Form from '~/components/atoms/forms/basis/Form';
import SimpleNavigation from '~/components/patterns/navigations/simpleNavigation/SimpleNavigation';
import SimpleNavigationItem from '~/components/patterns/navigations/simpleNavigation/SimpleNavigationItem';
import SplitButton, {
	SplitButtonSize
} from '~/components/patterns/buttons/SplitButton';

import useLocalStorage from '~/hooks/useLocalStorage';



const messages = defineMessages({
	close: {
		id: 'ui.general.close',
	},
	download: {
		id: 'ui.exportForm.download',
	},
	exportCsv: {
		id: 'ui.exportForm.exportCsv',
	},
	exportInProgress: {
		id: 'ui.exportForm.exportInProgress',
	},
	exportIsBeingPrepared: {
		id: 'ui.exportForm.exportIsBeingPrepared',
	},
	exportXlsx: {
		id: 'ui.exportForm.exportXlsx',
	},
	description: {
		id: 'ui.exportForm.description',
	},
	startExport: {
		id: 'ui.exportForm.startExport',
	},
	title: {
		id: 'ui.exportForm.title',
	},
});



type Props = {
	createExport: (format: GraphQL.ExportFormat) => Promise<{
		exportDownloadUrl: string,
		exportFilename: string,
		exportId: number,
		isLarge: boolean,
	}>,
	onCancelCallback: () => void,
};



const ExportForm: React.FC<Props> = (props) => {
	const {
		createExport,
		onCancelCallback,
	} = props;

	const [exportFormat, setExportFormat] = useLocalStorage('exportFormat', GraphQL.ExportFormat.Csv);

	const [exportResult, setExportResult] = React.useState<{
		exportDownloadUrl: string,
		exportFilename: string,
		exportId: number,
		isLarge: boolean,
	} | null>(null);

	const handleSubmitForm = React.useCallback(
		async () => {
			const exportData = await createExport(exportFormat);

			setExportResult(exportData);
		},
		[
			createExport,
			exportFormat,
			setExportResult,
		],
	);

	if (exportResult !== null) {
		let exportDownloadUrl = ASSETS_STORAGE_VERSIONLESS
			+ '/export.html'
			+ '?export=' + exportResult.exportFilename
			+ '&lang=' + 'en-US'
			+ '&download=' + exportResult.exportDownloadUrl;

		if (exportResult.isLarge) {
			exportDownloadUrl += '&is_large=true';
		}

		return (
			<ExportPanel
				description={(
					<FormattedMessage {...messages.exportIsBeingPrepared} />
				)}
				title={(
					<FormattedMessage {...messages.exportInProgress} />
				)}
			>
				<ButtonsLayout>
					<CancelButton onClickCallback={onCancelCallback}>
						<FormattedMessage {...messages.close} />
					</CancelButton>

					<Button
						href={exportDownloadUrl.toString()}
						size={ButtonSize.Medium}
						style={ButtonStyle.Action}
						uppercase={false}
					>
						<FormattedMessage {...messages.download} />
					</Button>
				</ButtonsLayout>
			</ExportPanel>
		);
	}

	return (
		<ExportPanel
			description={(
				<FormattedMessage
					{...messages.description}
					values={{
						exportFormat,
					}}
				/>
			)}
			title={(
				<FormattedMessage
					{...messages.title}
					values={{
						exportFormat,
					}}
				/>
			)}
		>
			<Form
				defaultDataHasChanged={true}
				onSuccess={handleSubmitForm}
			>
				{({ submit, isSubmitting }) => (
					<ButtonsLayout>
						<CancelButton onClickCallback={onCancelCallback} />
						<SplitButton
							dropdown={(
								<SimpleNavigation>
									{exportFormat !== GraphQL.ExportFormat.Csv && (
										<SimpleNavigationItem
											onClickCallback={() => setExportFormat(GraphQL.ExportFormat.Csv)}
										>
											<FormattedMessage {...messages.exportCsv} />
										</SimpleNavigationItem>
									)}
									{exportFormat !== GraphQL.ExportFormat.Xlsx && (
										<SimpleNavigationItem
											onClickCallback={() => setExportFormat(GraphQL.ExportFormat.Xlsx)}
										>
											<FormattedMessage {...messages.exportXlsx} />
										</SimpleNavigationItem>
									)}
								</SimpleNavigation>
							)}
							isDisabled={isSubmitting}
							onClick={() => submit()}
							size={SplitButtonSize.Medium}
							style={isSubmitting ? ButtonStyle.Inactive : ButtonStyle.Action}
							uppercase={false}
						>
							{exportFormat === GraphQL.ExportFormat.Xlsx ? (
								<FormattedMessage {...messages.exportXlsx} />
							) : (
								<FormattedMessage {...messages.exportCsv} />
							)}
						</SplitButton>
					</ButtonsLayout>
				)}
			</Form>
		</ExportPanel>
	)
};



export default ExportForm;
